import { common } from '@kurtosys/ksys-app-template';
import { IRequestOptions } from '@kurtosys/types/api/common/IRequestOptions.js';
import { IHandleResponse } from '@kurtosys/types/api/IHandleResponse.js';

export async function handleResponse<TRequestBody>(
	response: Response,
	options: IRequestOptions<TRequestBody>,
	overrideOptions?: Partial<IRequestOptions<TRequestBody>>,
): Promise<IHandleResponse> {
	const mergedOptions = common.commonUtils.deepMergeObjects(options, overrideOptions);
	// Get the response content type so we can decide how to handle the response
	const rawContentType = response.headers.get('content-type') || '';
	// content type can have a list of attributes attached to split by ";", the first attribute would the content type.
	const rawContentTypeList = rawContentType.split(';');
	const contentType = rawContentTypeList.length > 0 ? rawContentTypeList[0] : '';

	// extract user country onto the window object, as we do not expose response headers via the API Client.
	if (mergedOptions.fetchUserCountry && response.headers.has('X-KSYS-COUNTRY')) {
		const countryCode = response.headers.get('X-KSYS-COUNTRY');
		if (mergedOptions.debug) {
			console.info('User Country Code', countryCode);
		}
		if (window) {
			window[common.constants.GLOBAL_USER_COUNTRY_KEY] = countryCode;
		}
	}

	// Set a default response type
	let responseType;

	if (mergedOptions.debug) {
		console.log('response content type', contentType);
		if (response.headers.has('X-KSYS-DEBUG')) {
			console.info('cloudflare debug', response.headers.get('X-KSYS-DEBUG'));
		}
	}

	/**
	 * Change response type for other content types
	 *
	 * Common Mime type reference https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
	 */
	switch (contentType) {
		case '':
			responseType = null;
			break;
		case 'application/json':
			responseType = 'json';
			break;
		case 'text/plain':
		case 'text/html':
			responseType = 'text';
			break;
		case 'text/csv':
		case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
		case 'application/msword':
		case 'application/vnd.ms-powerpoint':
		case 'application/vnd.ms-excel':
		case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
		case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
		case 'application/pdf':
		case 'application/zip':
		case 'image/png':
		case 'image/jpeg':
		case 'image/gif':
		case 'image/svg+xml':
		case 'data:image/png':
		case 'data:image/jpeg':
		case 'data:image/gif':
		case 'data:image/svg+xml':
			responseType = 'blob';
			break;
		default:
			responseType = 'buffer';
	}

	// Return a blank if the response is a 204
	if (response.status === 204) {
		return {} as any;
	}

	// Await the response according to response type, e.g. response.json(), response.text()
	const responseBody = responseType ? await response[responseType]() : response;

	// Throw an exception for responses in the 400 range
	if (response.status >= 400 && response.status < 500) {
		throw Error(`${response.status}:\n${JSON.stringify(responseBody)}`);
	}

	// Throw an error for responses not in the 200 range
	if (!response.ok) {
		throw Error(response.statusText);
	}

	// If everything is ok return the response body and request status
	return {
		responseBody,
		statusText: response.statusText,
		status: response.status,
	};
}
